import { MailOutlined, SearchOutlined, TagOutlined } from "@ant-design/icons"
import {
  Alert,
  Card,
  Divider,
  Flex,
  Modal,
  Skeleton,
  Space,
  Spin,
  Typography
} from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"

import useDebounce from "../../../hooks/use-debounce"
import { type BasePagination } from "../../../types/api.ts"
import { cn } from "../../../utils/cn.ts"
import { USER_STATUSES } from "../../TeamManagementPage/constants/user-statuses.ts"
import type { TeamMembersFilters } from "../../TeamManagementPage/redux/team-members.types.ts"
import { type TeamMember } from "../../TeamManagementPage/types/types.ts"
import { TextInput } from "../../../components/Input/TextInput.tsx"

const { Text, Title } = Typography

interface TransferOwnershipModalProps {
  open: boolean
  transferring: boolean
  onClose: () => void
  getUsers: (
    params: Partial<TeamMembersFilters>
  ) => Promise<BasePagination<TeamMember>>
  onConfirm: (newOwnerUserId: string) => void
}

interface UsersState {
  items: TeamMember[]
  count: number
  hasMore: boolean
  loading: boolean
}

const INITIAL_STATE: UsersState = {
  items: [],
  count: 0,
  hasMore: false,
  loading: false
}

export function TransferOwnershipModal({
  open,
  transferring,
  onClose,
  getUsers,
  onConfirm
}: TransferOwnershipModalProps) {
  const [search, setSearch] = useState("")
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [state, setState] = useState<UsersState>(INITIAL_STATE)

  const debouncedSearch = useDebounce(search, 500)
  const pageRef = useRef(1)

  const fetchUsers = useCallback(
    async (q: string, pageNum: number, append: boolean) => {
      pageRef.current = pageNum
      setState((prev) => ({ ...prev, loading: true }))

      try {
        const res = await getUsers({
          q,
          page: pageNum,
          status: USER_STATUSES.ACTIVE
        })

        setState((prev) => ({
          items: append ? [...prev.items, ...res.results] : res.results,
          count: res?.count ?? 0,
          hasMore: !!res?.next,
          loading: false
        }))
      } catch {
        setState((prev) => ({ ...prev, loading: false }))
      }
    },
    [getUsers]
  )

  const handleLoadMore = useCallback(() => {
    void fetchUsers(debouncedSearch, pageRef.current + 1, true)
  }, [debouncedSearch, fetchUsers])

  const getUserName = (user: TeamMember) => {
    return (
      [user?.first_name, user?.middle_name, user?.last_name]
        .filter(Boolean)
        .join(" ") || user?.email
    )
  }

  useEffect(() => {
    if (!open) return
    void fetchUsers(debouncedSearch, 1, false)
  }, [open, debouncedSearch, fetchUsers])

  useEffect(() => {
    if (open) return
    pageRef.current = 1
    setSearch("")
    setSelectedUserId(null)
    setState(INITIAL_STATE)
  }, [open])

  const { items, count, hasMore, loading } = state

  return (
    <Modal
      open={open}
      title="Transfer GCC Ownership"
      okText="Transfer Ownership"
      cancelText="Cancel"
      cancelButtonProps={{ disabled: transferring }}
      okButtonProps={{
        danger: true,
        disabled: !selectedUserId,
        loading: transferring
      }}
      onOk={() => selectedUserId && onConfirm(selectedUserId)}
      onCancel={onClose}
      width={700}
      destroyOnHidden
    >
      <Space direction="vertical" size={12} className="w-full">
        <Alert
          type="warning"
          showIcon
          message="This transfers full organizational control. You will no longer be the GCC Owner."
        />

        <TextInput
          disableAutoCapitalize
          placeholder="Search by name or email…"
          prefix={<SearchOutlined />}
          allowClear
          onChange={(e) => setSearch(e.target.value)}
          value={search}
          readOnly={loading}
        />

        {loading && !items.length ? (
          <Space direction="vertical" className="w-full" size={8}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton.Node
                key={i}
                className="w-full"
                style={{ height: 72 }}
                active
              />
            ))}
          </Space>
        ) : (
          <Spin spinning={loading}>
            <div
              id="transferOwnerScrollable"
              style={{ height: 360, overflowY: "auto" }}
              className="tailwind-scroll"
            >
              <InfiniteScroll
                scrollableTarget="transferOwnerScrollable"
                dataLength={items.length}
                next={handleLoadMore}
                hasMore={hasMore && !loading}
                loader={
                  <Skeleton.Node
                    className="w-full"
                    style={{ height: 72 }}
                    active
                  />
                }
                endMessage={
                  count > 0 ? (
                    <Divider plain className="text-xs">
                      {items.length} of {count}
                    </Divider>
                  ) : null
                }
              >
                {items.length === 0 ? (
                  <div className="text-center py-10">
                    <Text type="secondary">No team members found</Text>
                  </div>
                ) : (
                  items.map((user) => (
                    <Card
                      key={user.id}
                      size="small"
                      className={cn(
                        "mb-2 cursor-pointer transition-colors hover:bg-primary/5 border-primary/30",
                        selectedUserId === user.id &&
                          "border-primary outline outline-primary bg-primary/5"
                      )}
                      onClick={() => setSelectedUserId(user.id)}
                    >
                      <Flex gap={8} align="center">
                        <Flex vertical gap={2} className="flex-1 min-w-0">
                          <Title level={5} className="!mb-0 truncate">
                            {getUserName(user)}
                          </Title>
                          <Flex gap={12} wrap>
                            <Text
                              type="secondary"
                              className="text-xs inline-flex items-center gap-1"
                            >
                              <MailOutlined />
                              {user.email}
                            </Text>
                            {user.label_name && (
                              <Text
                                type="secondary"
                                className="text-xs inline-flex items-center gap-1"
                              >
                                <TagOutlined />
                                {user.label_name}
                              </Text>
                            )}
                          </Flex>
                        </Flex>
                      </Flex>
                    </Card>
                  ))
                )}
              </InfiniteScroll>
            </div>
          </Spin>
        )}

        {selectedUserId && (
          <Text type="secondary" className="text-xs">
            After confirmation the selected user becomes the new GCC Owner and
            you will receive the default fallback role.
          </Text>
        )}
      </Space>
    </Modal>
  )
}
